import React, {useEffect, useMemo, useRef, useState} from "react";
import './MatchCategoriesTreeVisualization.scss';
import Margin from "../../../utils/margin";
import * as d3 from "d3";
import {ScaleBand} from "d3";
import {CmpTaxonomyNode} from "./CmpTaxonomyNodeType";
import {D3GSelection} from "../../../utils/global";
import {
    CollapsibleIndentTreeBuilder,
    CollapsibleIndentTreeData,
    Options as TreeOptions
} from "../collapsible-indent-tree/CollapsibleIndentTree";
import {Observable} from "rxjs";
import {MatKpiTreeData} from "../../../services/ApiTypes";

export type TreeDataType = CmpTaxonomyNode
export type Tree = d3.HierarchyNode<TreeDataType>
export type Data = {
    left: CollapsibleIndentTreeData,
    right: CollapsibleIndentTreeData,
    connections: DataConn[],
}
export type DataConn = {
    leftId: string
    rightId: string
    initial: boolean
    created: boolean
    selected: boolean
}
type CurveData = {
    ps: [number, number][]
    d: DataConn
}


export type Options = {
    width: number
    height: number
    margin: Margin
    onClickData?: (d: Tree) => void
    circleSize: number
    circleMargin: number
    flowVisualizationPortion: number // Portion of the width used for labels
    leftLevelFilter?: Observable<number>
    rightLevelFilter?: Observable<number>
}

/**
 * TODO: This class is very obscure
 */
export type UpdateData = {
    points: Tree[],
    connections: DataConn[]
};

type Props = {
    data?: Data
    options?: Partial<Options>
    updateData?: UpdateData
}
export const MatchCategoriesTreeVisualization: React.FC<Props> = ({data, options, updateData}) => {
    const svgRef = useRef<SVGSVGElement>(null)
    const o = useMemo<Options>(() => ({
        width: 1800,
        height: 0,
        margin: {
            left: 0,
            right: 0,
            top: 15,
            bottom: 15,
        },
        circleSize: 10,
        circleMargin: 15,
        flowVisualizationPortion: 6 / 12,
        ...options,
    }), [options]);

    const [controller, setController] = useState<MatchCategoriesTreeVisualizationController | null>(null);
    useEffect(() => {
        if (controller || !svgRef.current || !data) return;
        const svg = d3.select(svgRef.current as SVGElement)
        const root = svg.append('g')
        setController(new MatchCategoriesTreeVisualizationController(root, o, data))
    }, [data, svgRef, o, controller])

    useEffect(() => {
        if (!(controller && updateData)) return
        controller.update(updateData);
    }, [controller, updateData])

    return <svg
        className="match-categories-viz"
        ref={svgRef}
        viewBox={`0 0 ${o.width} ${o.height}`}
        style={{width: '100%', height: 'auto'}}
    />;
}

/**
 * TODO: Add documentation to this class
 */
class MatchCategoriesTreeVisualizationController {
    private readonly leftTree: CollapsibleIndentTreeBuilder<MatKpiTreeData>;
    private readonly rightTree: CollapsibleIndentTreeBuilder<MatKpiTreeData>;

    private readonly curveGroup: D3GSelection<any>;
    private readonly labelGroup: D3GSelection<any>;
    private readonly leftYAxis: ScaleBand<string>;
    private readonly rightYAxis: ScaleBand<string>;

    // private curve = d3.curveBundle.beta(0.9);
    private readonly curve = d3.curveBasis;
    // private curve = d3.curveLinear;

    // TODO: This can be condensed by a lot!
    private readonly labelSize: number;
    private readonly gap: number;
    private readonly selectionGap: number;

    private readonly lX0: number;
    private readonly lX1: number;
    private readonly rX0: number;
    private readonly rX1: number;

    private readonly lCircleX: number;
    private readonly lCircleXin: number;
    private readonly rCircleX: number;
    private readonly rCircleXin: number;
    private readonly effectiveGap: number;
    private readonly curveP0: number;
    private readonly curveP1: number;
    private readonly curveP2: number;
    private readonly curveP4: number;

    constructor(
        private root: D3GSelection<any>,
        private options: Options,
        private data: Data,
    ) {
        // console.log('new MatchCategoriesTreeVisualizationController()')
        this.root
            .classed('match-category-tree-viz', true)
            .classed('root', true)
            .attr("transform", "translate(" + this.options.margin.left + "," + this.options.margin.top + ")")

        const graphWidth = this.options.width - this.options.margin.left - this.options.margin.right;
        const graphHeight = this.options.height - this.options.margin.top - this.options.margin.bottom;

        this.labelSize = (1 - this.options.flowVisualizationPortion) / 2 * graphWidth;
        this.gap = this.options.flowVisualizationPortion * graphWidth;
        this.selectionGap = this.gap * 0.2;

        this.lX0 = 0;
        this.lX1 = this.labelSize;
        this.rX0 = this.labelSize + this.gap;
        this.rX1 = graphWidth;

        // this.root.append('rect')
        //     .attr('width', this.labelSize)
        //     .attr('height', graphHeight * 1 / 5);
        // this.root.append('rect')
        //     .attr('x', this.labelSize)
        //     .attr('width', this.gap)
        //     .attr('height', graphHeight * 2 / 5);

        this.lCircleX = this.lX1 + this.options.circleMargin + this.options.circleSize / 2;
        this.lCircleXin = this.lCircleX + this.options.circleSize / 2;
        this.rCircleX = this.rX0 - this.options.circleMargin - this.options.circleSize / 2;
        this.rCircleXin = this.rCircleX - this.options.circleSize / 2;
        this.effectiveGap = this.rCircleXin - this.lCircleXin;
        this.curveP0 = this.lCircleXin;
        this.curveP1 = this.lCircleXin + this.effectiveGap * 0.33;
        this.curveP2 = this.rCircleXin - this.effectiveGap * 0.33;
        this.curveP4 = this.rCircleXin;

        const leftTreeGroup = this.root.append('g')
            .classed('category-tree left', true)
        const treeOptionsLeft: TreeOptions<MatKpiTreeData> = {
            height: graphHeight,
            width: this.labelSize,
            margin: {
                left: this.lX0,
                right: 0,
                top: 0,
                bottom: 0,
            },
            $filter: {byLevel: options.leftLevelFilter},
            leftToRight: false,
            columns: [],
        }
        this.leftTree = new CollapsibleIndentTreeBuilder(leftTreeGroup, treeOptionsLeft, data.left);
        this.leftYAxis = d3.scaleBand()
            .domain(this.leftTree.getNodesOrdered().map(d => String(d.data.id)))
            .range([0, graphHeight])
        this.leftTree.setYAxis(this.leftYAxis)

        const rightTreeGroup = this.root.append('g')
            .classed('category-tree right', true)
        const treeOptionsRight: TreeOptions<MatKpiTreeData> = {
            height: graphHeight,
            width: this.labelSize,
            margin: {
                left: this.rX0,
                right: 0,
                top: 0,
                bottom: 0,
            },
            $filter: {byLevel: options.rightLevelFilter},
            leftToRight: true,
            columns: []
        }
        this.rightTree = new CollapsibleIndentTreeBuilder(rightTreeGroup, treeOptionsRight, data.right);
        this.rightYAxis = d3.scaleBand()
            .domain(this.rightTree.getNodesOrdered().map(d => String(d.data.id)))
            .range([0, graphHeight])
        this.rightTree.setYAxis(this.rightYAxis)

        this.leftTree.update();
        this.rightTree.update();

        this.curveGroup = this.root.append('g')
            .classed('curved', true)

        this.labelGroup = this.root.append('g')
            .classed('labels', true)
    }

    update(u?: UpdateData) {
        if (u) {
            this.drawCurves(u.connections);
            this.drawPoints(u.points, u.connections)
        }
    }

    drawCurves(cmpConnectionData: DataConn[]) {
        const cData: CurveData[] = cmpConnectionData.map((d) => ({
            ps: [
                [this.curveP0, this.leftYAxis(String(d.leftId))],
                [this.curveP1, this.leftYAxis(String(d.leftId))],
                [this.curveP2, this.rightYAxis(String(d.rightId))],
                [this.curveP4, this.rightYAxis(String(d.rightId))],
            ] as [number, number][],
            d,
        } as CurveData));

        // const line = d3.line<CurveData>(d => d.ps[0], d => d.ps[1])
        const line = d3.line()
            .curve(this.curve)

        this.curveGroup
            .selectAll<SVGGElement, CurveData>('g.curve')
            // .data(cData)
            .data(cData, d => `${d.d.leftId}|${d.d.rightId}`)
            .join(
                enter => {
                    const g = enter.append('g');
                    g.append('path')
                        .attr('d', d => line(d.ps))
                    return g;
                },
                update => update,
                exit => exit.remove(),
            )
            .classed('curve', true)
            .classed('initial', d => d.d.initial)
            .classed('created', d => d.d.created)
            .classed('selected', d => d.d.selected)


        // const flows = this.root
        //     .append('g')
        //     .classed('flows', true)
        //     .selectAll('g.flow')
        //     .data(data)
        //     .join('g')
        //     .classed('flow', true);
        // const flowData: FlowData[] = [...Array(data.length - 1)].map((_, i) => {
        //     const prev: Tree = data[i];
        //     const next: Tree = data[i + 1];
        //     return {
        //         i: i as number,
        //         left: [
        //             {i: 0, v: prev.leftRest.v, dy: -overlap},
        //             {i: 0, v: prev.leftRest.v, dy: 0},
        //             {i: 0, v: prev.leftRest.v, dy: overlap},
        //             {i: 1, v: next.leftRest.v, dy: -overlap},
        //             {i: 1, v: next.leftRest.v, dy: 0},
        //             {i: 1, v: next.leftRest.v, dy: overlap},
        //         ] as any[],
        //         right: [
        //             {i: 0, v: prev.rightCommon.v, dy: -overlap},
        //             {i: 0, v: prev.rightCommon.v, dy: 0},
        //             {i: 0, v: prev.rightCommon.v, dy: overlap},
        //             {i: 1, v: next.rightCommon.v, dy: -overlap},
        //             {i: 1, v: next.rightCommon.v, dy: 0},
        //             {i: 1, v: next.rightCommon.v, dy: overlap},
        //         ] as any[],
        //     }
        // })
        //
        // const flowLeft = d3.area<any>()
        //     .curve(curve)
        //     .x0(splitLocL - LINE_WIDTH)
        //     .x1(({i, v}) => (xAxises[i].left)(v) as number + LINE_WIDTH)
        //     .y(({i, v, dy}) => (
        //         i === 0 ? yAxis(`${DATA_OFFSET + i}`) as number + yAxis.bandwidth() * (1 - BAR_PADDING) : yAxis(`${DATA_OFFSET + i}`) as number
        //     ) + dy)
        // flows.append('path')
        //     .classed('left', true)
        //     .attr('d', flowLeft(flowData[DATA_OFFSET].left) as string)
        //
        // const flowRight = d3.area<any>()
        //     .curve(curve)
        //     .x0(splitLocR + LINE_WIDTH)
        //     .x1(({i, v}) => (xAxises[i].right)(v) as number - LINE_WIDTH)
        //     .y(({i, v, dy}) => (
        //         i === 0 ? yAxis(`${DATA_OFFSET + i}`) as number + yAxis.bandwidth() * (1 - BAR_PADDING) : yAxis(`${DATA_OFFSET + i}`) as number
        //     ) + dy)
        // flows.append('path')
        //     .classed('right', true)
        //     .attr('d', flowRight(flowData[DATA_OFFSET].right) as string)
    }

    drawPoints(pointData: Tree[], connectionData: DataConn[]) {
        const onClick = this.options.onClickData?.bind(this);
        const outerCircleRadius = this.options.circleSize / 2;
        const innerCircleRadius = outerCircleRadius * .60;

        this.labelGroup
            .selectAll('g.label')
            .data(pointData)
            .join(
                enter => {
                    const g = enter.append('g');

                    // // For selection overlay over the labels of the tree's
                    // g.append('rect')
                    //     .classed('hidden', true)
                    //     .attr('x', d => d.data.isLeft ? this.lX0 : this.rX0 - this.gap / 2 + this.selectionGap / 2)
                    //     .attr('y', d => (d.data.isLeft
                    //             ? (this.leftYAxis(String(d.data.id)) || 0) - this.leftYAxis.bandwidth() / 2
                    //             : (this.rightYAxis(String(d.data.id)) || 0) - this.rightYAxis.bandwidth() / 2
                    //     ))
                    //     .attr('width', this.labelSize + this.gap / 2 - this.selectionGap / 2)
                    //     .attr('height', d => d.data.isLeft ? this.leftYAxis.bandwidth() : this.rightYAxis.bandwidth())
                    // For selection overlay only on the selection boxes

                    const hoverOverlayMargin = this.options.circleMargin / 2
                    g.append('rect')
                        .classed('hidden', true)
                        .attr('x', d => d.data.isLeft ? this.lX1 + hoverOverlayMargin : this.rX0 - this.gap / 2 + this.selectionGap / 2)
                        .attr('y', d => (d.data.isLeft
                                ? (this.leftYAxis(String(d.data.id)) || 0) - this.leftYAxis.bandwidth() / 2
                                : (this.rightYAxis(String(d.data.id)) || 0) - this.rightYAxis.bandwidth() / 2
                        ))
                        .attr('width', this.gap / 2 - this.selectionGap / 2 - hoverOverlayMargin)
                        .attr('height', d => d.data.isLeft ? this.leftYAxis.bandwidth() : this.rightYAxis.bandwidth())


                    g.append('circle')
                        .classed('outer', true)
                        .attr('cx', d => d.data.isLeft ? this.lCircleX : this.rCircleX)
                        .attr('cy', d => (d.data.isLeft
                                ? this.leftYAxis(String(d.data.id)) as number// + leftYAxis.bandwidth() / 2
                                : this.rightYAxis(String(d.data.id)) as number// + rightYAxis.bandwidth() / 2
                        ))
                        .attr('r', outerCircleRadius)
                    g.append('circle')
                        .classed('inner', true)
                        .attr('cx', d => d.data.isLeft ? this.lCircleX : this.rCircleX)
                        .attr('cy', d => (d.data.isLeft
                                ? this.leftYAxis(String(d.data.id)) as number// + leftYAxis.bandwidth() / 2
                                : this.rightYAxis(String(d.data.id)) as number// + rightYAxis.bandwidth() / 2
                        ))
                        .attr('r', innerCircleRadius)

                    return g;
                },
                update => update,
                exit => exit.remove(),
            )
            .classed('label', true)
            .classed('left', d => d.data.isLeft)
            .classed('right', d => !d.data.isLeft)
            .classed('selected', d => d.data.selected)
            .classed('connected-not-init', d => d.data.isLeft
                ? Boolean(connectionData.find(c => c.leftId === d.data.id && !c.initial))
                : Boolean(connectionData.find(c => c.rightId === d.data.id && !c.initial))
            )
            .classed('connected-as-init', d => d.data.isLeft
                ? Boolean(connectionData.find(c => c.leftId === d.data.id && c.initial))
                : Boolean(connectionData.find(c => c.rightId === d.data.id && c.initial))
            )
            .on('click', function () {
                const d = d3.select(this).datum() as Tree;
                if (onClick)
                    onClick(d)
            })

        // labelGroup.append('text')
        //     .attr('dominant-baseline', 'middle')
        //     .attr("text-anchor", d => d.data.left ? 'end' : 'start')
        //     .attr('x', d => d.data.left ? lX1 : rX0)
        //     .attr('y', d => d.data.left ? leftYAxis(String(d.data.id)) as number : rightYAxis(String(d.data.id)) as number)
        //     .text(d => d.data.label || '')
        // // .attr('font-family', 'FontAwesome')
        // // .text(d => '\uf118')
        // // Process the data
        // const allValues = data.reduce<number[]>((arr, d) => arr.concat(getBoxValues(d.box)), []);
        // const max = d3.max(allValues) || 1;
        //
        // const HIDE_ZEROLINE = false;
        // const min = HIDE_ZEROLINE ? 0 : (d3.min(allValues) || 0);
        //
        // // set the range and domain for the axis
        // let catAxis = d3.scaleBand()
        //     .domain(data.map(d => d.name))
        //     .range([0, graphWidth])
        // const BAR_PADDING = 0.2;
        // const BAR_SPACING = BAR_PADDING / 2 * catAxis.bandwidth();
        //
        // const valRange = [graphHeight, 0]
        // let valueAxis = d3.scaleLinear()
        //     .domain([min, max])
        //     .range(valRange)
        //
        // // append the rectangles for the bar chart
        // const barGroups = this.root
        //     .append('g')
        //     .classed('data', true)
        //     .selectAll('g.box-wrapper')
        //     .data(data)
        //     .join('g')
        //     .classed('box-wrapper', true);
        //
        // // Make sure the parent-group size to the full field
        // barGroups.append('rect')
        //     .attr('x', d => (catAxis(d.name) as number))
        //     .attr('y', 0)
        //     .attr('width', catAxis.bandwidth())
        //     .attr('height', graphHeight)
        //     .classed('hover-overlay', true)
        //
        // barGroups.on('mouseenter', function (event) {
        //     const data = d3.select(this).datum()
        //     console.log('data', data);
        //     // const [x, y] = d3.pointer(event)
        //     // var mouse = d3.mouse(this);
        //     // var x = mouse[0];
        //     // var y = mouse[1];
        //
        //     // console.log('x, y', x, y);
        //
        // })
        //
        // if (onCategoryClick) {
        //     barGroups
        //         .classed('clickable', true)
        //         .on('click', function () {
        //             const data = d3.select(this).datum() as BoxplotDataPoint;
        //             onCategoryClick(data);
        //         })
        // }
        //
        // // Draw the boxplot
        // // - Main line
        // barGroups.append('line')
        //     .classed('backbone', true)
        //     .attr("x1", d => (catAxis(d.name) as number) + catAxis.bandwidth() / 2)
        //     .attr("x2", d => (catAxis(d.name) as number) + catAxis.bandwidth() / 2)
        //     .attr("y1", d => valueAxis(d.box.q0) as number)
        //     .attr("y2", d => valueAxis(d.box.q4) as number)
        // // - Box
        // barGroups.append('rect')
        //     .classed('box', true)
        //     .attr("x", d => (catAxis(d.name) as number) + BAR_SPACING)
        //     .attr("y", d => valueAxis(d.box.q3) as number)
        //     .attr("width", catAxis.bandwidth() * (1 - BAR_PADDING))
        //     .attr("height", d => valueAxis(d.box.q1) - valueAxis(d.box.q3))
        //     .attr('fill', d => vizColor(d.name))
        // // - Horizontal lines
        // barGroups.append('line')
        //     .classed('line line-min', true)
        //     .attr("x1", d => (catAxis(d.name) as number) + BAR_SPACING)
        //     .attr("x2", d => (catAxis(d.name) as number) + catAxis.bandwidth() - BAR_SPACING)
        //     .attr("y1", d => valueAxis(d.box.q0) as number)
        //     .attr("y2", d => valueAxis(d.box.q0) as number)
        // barGroups.append('line')
        //     .classed('line line-med', true)
        //     .attr("x1", d => (catAxis(d.name) as number) + BAR_SPACING)
        //     .attr("x2", d => (catAxis(d.name) as number) + catAxis.bandwidth() - BAR_SPACING)
        //     .attr("y1", d => valueAxis(d.box.q2) as number)
        //     .attr("y2", d => valueAxis(d.box.q2) as number)
        // barGroups.append('line')
        //     .classed('line line-max', true)
        //     .attr("x1", d => (catAxis(d.name) as number) + BAR_SPACING)
        //     .attr("x2", d => (catAxis(d.name) as number) + catAxis.bandwidth() - BAR_SPACING)
        //     .attr("y1", d => valueAxis(d.box.q4) as number)
        //     .attr("y2", d => valueAxis(d.box.q4) as number)
        //
        // // Add the horizontal Axis
        // this.root.append("g")
        //     .classed('category-axis', true)
        //     .attr("transform", "translate(0," + graphHeight + ")")
        //     .call(d3.axisBottom(catAxis)
        //             // .tickFormat(() => '') // Hide the labels
        //             .tickSize(0) // Hide ticks
        //         // .tickValues([]) // Hide the ticks AND labels
        //     )
        //     .call(g => g.select('.domain').remove())
        // if (min !== 0) {
        //     this.root.append('g')
        //         .classed('zero-axis', true)
        //         .append('line')
        //         .attr("x1", 0)
        //         .attr("x2", graphWidth)
        //         .attr("y1", valueAxis(0) as number)
        //         .attr("y2", valueAxis(0) as number)
        // }
        //
        // // Add the vertical axis
        // // Build sensible graph for values [0-100], [0-1000], [0-10K], [0-100K], etc
        // this.root.append("g")
        //     .classed('value-axis', true)
        //     .call(
        //         d3.axisLeft(valueAxis)
        //             .ticks(5)
        //             .tickFormat(v => d3.format("~s")(v))
        //     )
    }
}
